import { Link } from 'react-router-dom'
import { useI18n } from '../lib/i18n'

/**
 * /terms + /privacy — plain-language legal pages.
 * Kept honest and short: what the partner program is, what we store, who to
 * contact. No boilerplate walls a non-lawyer can't read.
 */

export function Terms() {
  const { t } = useI18n()
  return (
    <LegalShell title={t('Terms of Service')} updated="2026-07-17">
      <H2>{t('1. What Qordo Catalog is')}</H2>
      <P>
        {t('Qordo Catalog (getqordo.com) is a referral partner program operated by MyClienta (myclienta.com). Partners browse working AI product demos, show them to local businesses, and submit leads. When a referred business subscribes, the partner earns a commission.')}
      </P>
      <H2>{t('2. The commission deal')}</H2>
      <P>
        {t('€30 one-time setup fee per closed deal (100% to the referring partner) plus 30% of the referred business\'s monthly subscription for the first 6 months. Commissions are paid monthly, on or around the 15th, once the referred business has actually paid. No payment from the business — no commission. We may change rates for future deals with notice; closed deals keep the terms they closed under.')}
      </P>
      <H2>{t('3. Partner conduct')}</H2>
      <P>
        {t('Don\'t misrepresent the products, invent features, spam, or promise prices we haven\'t published. Leads must be real businesses that actually spoke with you. Fake or self-referred leads void their commissions and can end the partnership.')}
      </P>
      <H2>{t('4. Accounts')}</H2>
      <P>
        {t('Joining is free. You are responsible for keeping your login credentials private. We can suspend accounts that break these terms. You can delete your account any time by emailing us.')}
      </P>
      <H2>{t('5. No guarantees')}</H2>
      <P>
        {t('Demos are provided "as is" for demonstration. We don\'t guarantee any income — earnings depend entirely on the deals you close.')}
      </P>
      <H2>{t('6. Contact')}</H2>
      <P>
        {t('Questions:')} <a className="text-brand-400 underline" href="mailto:hello@myclienta.com">hello@myclienta.com</a>
      </P>
    </LegalShell>
  )
}

export function Privacy() {
  const { t } = useI18n()
  return (
    <LegalShell title={t('Privacy Policy')} updated="2026-07-17">
      <H2>{t('What we store')}</H2>
      <P>
        {t('For partner accounts: your name, email, a hashed password (never plain text), your referral code, and the leads and commissions linked to your account. For the waitlist: the email you submit. That\'s the full list.')}
      </P>
      <H2>{t('What we don\'t do')}</H2>
      <P>
        {t('We don\'t sell your data, run third-party ad trackers, or share your details with anyone except the payment processing needed to pay you.')}
      </P>
      <H2>{t('Where it lives')}</H2>
      <P>
        {t('Data is stored in Cloudflare\'s D1 database (EU jurisdiction infrastructure). Traffic passes through Cloudflare\'s network with standard security headers and HTTPS enforced.')}
      </P>
      <H2>{t('Analytics')}</H2>
      <P>
        {t('We count page visits with a first-party beacon plus Cloudflare Web Analytics — both cookieless, no fingerprinting, no advertising trackers.')}
      </P>
      <H2>{t('Your rights')}</H2>
      <P>
        {t('Email us to see, correct, or delete everything we hold about you. We answer within 30 days:')} <a className="text-brand-400 underline" href="mailto:hello@myclienta.com">hello@myclienta.com</a>
      </P>
    </LegalShell>
  )
}

function LegalShell({ title, updated, children }: { title: string; updated: string; children: React.ReactNode }) {
  const { t } = useI18n()
  return (
    <section className="section py-12 max-w-3xl">
      <h1 className="text-3xl font-bold text-white">{title}</h1>
      <p className="mt-1 text-sm text-ink-500">{t('Last updated')} {updated}</p>
      <div className="card mt-6">{children}</div>
      <p className="mt-6 text-sm text-ink-500">
        {t('See also:')} <Link to="/terms" className="text-brand-400 underline">{t('Terms')}</Link> ·{' '}
        <Link to="/privacy" className="text-brand-400 underline">{t('Privacy')}</Link> ·{' '}
        <Link to="/how-it-works" className="text-brand-400 underline">{t('How the program works')}</Link>
      </p>
    </section>
  )
}

function H2({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-5 first:mt-0 text-lg font-semibold text-white">{children}</h2>
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-2 text-sm leading-relaxed text-ink-100">{children}</p>
}
